import store from 'store'
import i18n from 'i18n'
import history from 'history'

import { store as reduxStore } from 'index'
import { getEnvParam, removeEnvParam, setTempEnvParam } from 'env'
import { errorBase } from 'utils/ui'
import { notifyExtensionLogin } from 'utils/extensionBridge'
import { sentrySetUser } from 'sentry'

import { safeboxClient, safeboxClientNoToken, authClient, authClientRequiredToken } from 'myNet'

// 초대 메일 시 호출되는 코드
// invite info
export async function basicInviteInfo(token) {
  const promise = new Promise((resolve, reject) => {
    safeboxClientNoToken
      .get(`/v1/users/invite/info`, {
        token,
      })
      .then((res) => {
        if (res.data.code === 200) {
          if (res.data.data?.flow_type === 'join') {
            errorBase({
              title: i18n.t('basic.desc.inviteFailed', {
                defaultValue: 'ワークスペースの招待に失敗しました',
              }),
              content: i18n.t('workspace.error.880', {
                defaultValue: 'すでに他のワークスペースに所属しています。',
              }),
              okText: i18n.t('common.button.ok', { defaultValue: '確認' }),
            })
          }
          resolve(res.data)
        } else {
          resolve(res.data)
          errorBase({
            title: i18n.t('basic.desc.inviteFailed', {
              defaultValue: 'ワークスペースの招待に失敗しました',
            }),
            content: i18n.t(`workspace.error.${res.data.code}`, {
              defaultValue: '期限切れの招待です。',
            }),
            okText: i18n.t('common.button.ok', { defaultValue: '確認' }),
          })
        }
      })
      .catch((error) => {
        reject(error)
      })
  })
  return promise
}

// basic register
export async function basicRegister(request) {
  const promise = new Promise((resolve, reject) => {
    authClient
      .post('/auth/basic-register', {
        email: request.email,
        full_name: request.full_name,
        password: request.password,
        workspace_name: request.workspace_name,
        service_group_code: 'safebox',
        is_active: true,
        auth_type: 'basic',
        provider: 'local',
      })
      .then((res) => {
        if (res.data.code === 200) {
          store.set('app.user.token', res.data?.data?.access_token)
          store.set('app.user.refreshToken', res.data?.data?.refresh_token)
          notifyExtensionLogin()

          if (res.data?.data?.workspace != null) {
            store.set('app.user.workspace_id', res.data?.data?.workspace?.id)
          } else {
            // 일반 가입은 새 워크스페이스 생성이므로 이전 세션의 workspace_id 가 남아 있으면 안 된다.
            // 잔존 시 restClient 가 모든 요청에 workspace-id 헤더로 첨부하고, 백엔드 미들웨어가
            // JWT claim 보다 헤더를 우선해 auth/info 의 wrkp_initialized 가 이전 워크스페이스
            // 기준으로 계산됨 → masterPW/register 에 server(ESCROW) 복구 옵션이 오노출된다.
            store.remove('app.user.workspace_id')
          }

          if (res.data?.data?.user != null) {
            store.set('app.user.id', res.data?.data?.user?.id)
            store.set('app.user.name', res.data?.data?.user?.full_name)
            store.set('app.user.email', res.data?.data?.user?.email)
          }

          // 나중에 제거될 수도 있음
          store.set('app.user.officenext', false)

          resolve('success')
        }
      })
      .catch((error) => {
        reject(error)
      })
  })

  return promise
}

// --- basic invite accept/register ---
export async function basicInviteCommonAuth(request) {
  const inviteType = JSON.parse(getEnvParam('inviteType') || '{}')
  const REGISTER_TYPE = request.type === 'register'

  const endpoint = REGISTER_TYPE ? '/user/workspaces/register' : '/user/workspaces/accept'
  const client = REGISTER_TYPE ? authClient : authClientRequiredToken

  const originRequest = {
    email: inviteType.invitee_email,
    password: request.password,
    invited_workspace_id: inviteType.workspace_id,
    invited_role: inviteType.role,
    service_group_code: 'safebox',
    ...(REGISTER_TYPE && { full_name: request.full_name }),
  }

  const promise = new Promise((resolve, reject) => {
    client
      .post(endpoint, originRequest)
      .then((res) => {
        if (res.data?.code === 200) {
          store.set('app.user.token', res.data?.data?.access_token)
          store.set('app.user.refreshToken', res.data?.data?.refresh_token)
          notifyExtensionLogin()

          if (REGISTER_TYPE) {
            store.set('app.user.name', request.full_name)
          } else {
            store.set('app.user.workspace_id', inviteType.workspace_id)
          }

          resolve(true)
        } else {
          reject(new Error(res.data.message))
        }
      })
      .catch((error) => {
        reject(error)
      })
  })

  return promise
}

// basic login
export async function basicLogin(email, password, saveToken = false) {
  const request = {
    email,
    password,
    service_group_code: 'safebox',
  }

  const promise = new Promise((resolve, reject) => {
    authClient
      .post('/auth/token', request)
      .then((res) => {
        console.log(' --- res : ', res)

        if (res.data.code === 200) {
          const data = res.data?.data

          if (saveToken) {
            store.set('app.user.token', data?.access_token)
            store.set('app.user.refreshToken', data?.refresh_token)
            store.set('app.user.name', data?.full_name)
            notifyExtensionLogin()
          } else {
            setTempEnvParam('tokenInfo', data)
          }

          store.set('app.user.email', email)

          sentrySetUser(email, email)

          if (data?.is_required_password_change === true) {
            store.set('app.user.token', data?.access_token)
            store.set('app.user.refreshToken', data?.refresh_token)
            store.set('app.user.name', data?.full_name)
            notifyExtensionLogin()
            resolve('changePW')
            return
          }

          reduxStore.dispatch({
            type: 'settings/CHANGE_SETTING',
            payload: {
              setting: 'showZeroDarkWeb',
              value: true,
            },
          })

          resolve(true)
        } else {
          errorBase({
            title: i18n.t('admin.activity.failedLogin'),
            content: i18n.t('basic.login.incorrectPassword'),
            okText: i18n.t('common.button.ok'),
          })
          resolve(false)
        }
      })
      .catch((error) => {
        console.log(' --- error : ', error)
        const code = error?.origin?.code

        reject(new Error(error.message))

        if (code === 400) {
          errorBase({
            title: 'Error',
            content: 'Not Found Password',
          })
        } else {
          errorBase({
            title: 'Error',
            content: error.message,
          })
        }
      })
  })

  return promise
}

export async function basicInviteCommonAuthRegister(request) {
  const inviteType = JSON.parse(getEnvParam('inviteType') || '{}')

  const originRequest = {
    email: inviteType.invitee_email,
    password: request.password,
    invited_workspace_id: inviteType.workspace_id,
    invited_role: inviteType.role,
    service_group_code: 'safebox',
    full_name: request.full_name,
  }

  const promise = new Promise((resolve, reject) => {
    authClient.post('/user/workspaces/register', originRequest).then((res) => {
      if (res.data?.code === 200) {
        store.set('app.user.token', res.data?.data?.access_token)
        store.set('app.user.refreshToken', res.data?.data?.refresh_token)
        store.set('app.user.name', request.full_name)
        store.set('app.user.workspace_id', inviteType.workspace_id)
        notifyExtensionLogin()

        history.push('/masterPW/register')

        resolve(true)
      } else {
        reject(new Error(res.data.message))
      }
    })
  })

  return promise
}

export async function basicInviteCommonAuthAccept(request) {
  const inviteType = JSON.parse(getEnvParam('inviteType') || '{}')

  const originRequest = {
    email: inviteType.invitee_email,
    password: request.password,
    invited_workspace_id: inviteType.workspace_id,
    invited_role: inviteType.role,
    service_group_code: 'safebox',
  }
  const promise = new Promise((resolve, reject) => {
    authClient.post('/user/workspaces/accept', originRequest).then((res) => {
      if (res.data?.code === 200) {
        store.set('app.user.token', res.data?.data?.access_token)
        store.set('app.user.refreshToken', res.data?.data?.refresh_token)
        store.set('app.user.name', res.data?.data?.full_name)
        store.set('app.user.workspace_id', inviteType.workspace_id)
        notifyExtensionLogin()

        if (request.inviteType === 'register') {
          reduxStore.dispatch({
            type: 'user/LOAD_CURRENT_ACCOUNT',
          })
        }

        resolve(true)
      } else {
        let errorTitle = i18n.t('admin.activity.failedLogin')
        let errorContent = i18n.t('workspace.error.null')

        if (res.data.code === 632) {
          errorContent = i18n.t(`workspace.error.${res.data.code}`)
        } else if (res.data.message === 'Password is incorrect.') {
          errorContent = i18n.t('workspace.error.passwordIncorrect')
        } else if (res.data.code === 429) {
          errorTitle = i18n.t('workspace.error.429.title')
          errorContent = i18n.t('workspace.error.429.description')
        } else if (
          res.data?.message?.startsWith(
            'User setup is not completed. Please complete the initial workspace setup first.',
          )
        ) {
          errorContent = i18n.t('workspace.error.notCompleteCreateWorkspace')
        }

        errorBase({
          title: errorTitle,
          content: errorContent,
        })
        reject(new Error(res.data.message))
      }
    })
  })

  return promise
}

// basic invite accept -- safebox auth
export async function basicAcceptInvitedSafebox() {
  const inviteType = JSON.parse(getEnvParam('inviteType') || '{}')
  const promise = new Promise((resolve, reject) => {
    const inviteToken = store.get('app.user.invite_token')

    safeboxClient
      .post(`/v1/users/workspace-invite/${inviteToken}/accept`, {
        invitee_email: inviteType.invitee_email,
      })
      .then((res) => {
        if (res?.data?.code === 200) {
          reduxStore.dispatch({
            type: 'user/SET_STATE',
            payload: {
              noLogin: true,
            },
          })

          store.remove('app.user.invite_name')
          removeEnvParam('inviteType')

          reduxStore.dispatch({
            type: 'user/LOAD_CURRENT_ACCOUNT',
          })

          resolve(true)
        } else {
          if (res.data?.code === 880) {
            store.remove('app.user.refreshToken')
            store.remove('app.user.token')
          }

          errorBase({
            title: i18n.t('admin.activity.failedLogin'),
            content: i18n.t(`workspace.error.${res.data.code}`),
          })

          resolve(false)
        }
      })
      .catch((error) => {
        reject(error)
      })
  })
  return promise
}
